import { Category } from '../settings/enums';
import { isMobile } from 'react-device-detect';
import store from '../state/store';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actionCreators } from '../state';
import { useTracking } from 'react-tracking';
import { BookingState } from '../state/reducers/bookingReducer';
import { ProductState } from '../state/reducers/productsReducer';
import Helper from '../settings/helperFunctions';

export type TrackingPageLoadType = {
  name: string;
  conversionType: string;
};

type Tracking = {
  event: string;
  page: TrackingPage;
  product: Array<TrackingProduct>;
  transaction: TrackingTransaction;
  eventdetails?: TrackingEventDetails;
  miscellaneous?: TrackingMiscellaneous;
  user?: Array<TrackingUser>;
};

type TrackingPage = {
  pageInfo: {
    pageName?: string;
    sysEnv?: string | null;
    issueDate?: string;
  };
  attributes: {
    brokerModus: string;
    displayedBrand: string;
    language: string;
    viewPort?: string;
  };
  category: {
    baCategory: string;
    pagePurpose: string;
    pageType: string;
    primaryCategory: string;
  };
};

type TrackingProduct = {
  attributes: {
    insuranceStart?: string;
    insuranceEnd?: string;
    productBranch?: string;
    productCombination?: string;
    productOwner: string;
    insuranceAmount?: string;
    tariffOptions1?: string;
  };
  category: {
    primaryCategory: string;
  };
  productInfo: {
    productName: string;
  };
};

type TrackingTransaction = {
  transactionID?: string;
  vsnr?: string;
  attributes: {
    conversionType?: string;
    birthyearsOfTravellers?: string;
    destinationCountry?: string;
    travelDurationInDays?: string;
    travelStartDate?: string;
    daysToDeparture?: string;
    noOfTravellersFrom41To64Years?: number;
    noOfTravellersFrom26To64Years?: number;
    noOfTravellersUpTo25Years?: number;
    noOfTravellersFrom65Years?: number;
    noOfTravellersComplementary?: number;
    rentalProperty?: string;
    confirmationDate?: string;
    paymentMethod?: string;
    travelPrice?: number;
    noOfInsuredPeople?: number;
    travelEnd?: string;
    mainCountryOfTraveller?: string;
    calculatedValue?: string;
    travelPriceType: string;
    paymentInterval?: string;
  };
  total?: {
    transactionTotal: string;
  };
};

type TrackingEventDetails = {
  clickedElement?: string;
  clickType?: string;
};

type TrackingMiscellaneous = {
  agency: {
    oenrNumber: string;
  };
  errors?: TrackingError;
  owCompatibility?: {
    download: boolean;
  };
};

type TrackingUser = {
  profile: {
    address: {
      postalCode: string;
    };
    attributes: {
      gender: string;
      birthday: string;
      role: string;
      loggedIn: string;
    };
  };
};

type TrackingError = {
  errorFlag?: boolean;
  errorType?: string;
  errorMessages?: string;
  errorUrl?: string;
};

export type TrackingData = {
  event: string;
  pageName?: string;
  conversionType?: string;
  eventDetails?: TrackingEventDetails;
  download?: boolean;
  errorUrl?: string;
};

export const buildTrackingData = (tracking: TrackingData) => {
  const configData = store.getState().configReducer;
  const personData = store.getState().bookingReducer;
  const errorData = store.getState().errorReducer;
  const productsData = store.getState().productsReducer;
  const sysEnv = store.getState().settingsReducer.environment;
  const settingsData = store.getState().settingsReducer;
  const categoryTrackingId = store.getState().bookingReducer.categoryTrackingId;
  const { trackingType } = store.getState().configReducer.properties;
  if (!tracking.pageName) {
    tracking.pageName =
      configData.properties.trackingType + '_' + store.getState().errorReducer.trackingPageName;
  } else {
    tracking.pageName = configData.properties.trackingType + '_' + tracking.pageName;
  }
  let data: Tracking = {
    event: tracking.event,
    page: {
      category: {
        baCategory: configData.properties.productListName,
        pagePurpose: 'Sale',
        pageType: 'Tarifrechner',
        primaryCategory: configData.properties.trackingType,
      },
      attributes: {
        language: 'de', //TODO
        brokerModus: 'Off', // TODO
        displayedBrand: 'ERV',
        viewPort: isMobile ? 'mobil' : 'desktop',
      },
      pageInfo: {
        pageName: tracking.pageName,
        issueDate: trackingType + '|' + process.env.REACT_APP_VERSION?.split('|')[1],
        sysEnv: sysEnv,
      },
    },
    product: buildTrackingProducts(
      tracking.pageName,
      trackingType,
      productsData,
      personData,
      settingsData.currentSteps,
      categoryTrackingId,
    ),
    user: buildTrackingUsers(personData),
    eventdetails: tracking.eventDetails ? tracking.eventDetails : {},
    miscellaneous: {
      agency: {
        oenrNumber: settingsData.partnerId,
      },
      errors: errorData.errorInfoText
        ? {
            errorMessages: errorData.errorInfoText,
            errorFlag: true,
            errorType: errorData.errorType,
            errorUrl: window.location.href,
          }
        : undefined,
      owCompatibility: tracking.download ? { download: true } : undefined,
    },
    transaction: {
      attributes: {
        conversionType: tracking.conversionType,
        birthyearsOfTravellers: personData.persons
          .map((person) => person.birthDate?.split('-')[0])
          .join('|'),
        travelStartDate: personData.startDate.replaceAll('-', ''),
        travelEnd: personData.endDate.replaceAll('-', ''),
        destinationCountry:
          personData.geographicalZone === 'e'
            ? 'Europa'
            : personData.geographicalZone === 'w'
            ? 'Welt'
            : personData.geographicalZone === 'd'
            ? 'Deutschland'
            : personData.geographicalZone,
        noOfTravellersUpTo25Years: personData.travelers.children
          ? personData.travelers.children
          : 0,
        noOfTravellersFrom26To64Years: personData.travelers.adultsUpTo40
          ? personData.travelers.adultsUpTo40
          : 0,
        noOfTravellersFrom41To64Years: personData.travelers.adultsUpTo64
          ? personData.travelers.adultsUpTo64
          : 0,
        noOfTravellersFrom65Years: personData.travelers.adultsFrom65
          ? personData.travelers.adultsFrom65
          : 0,
        noOfTravellersComplementary: personData.travelers.complementary
          ? personData.travelers.complementary
          : 0,
        noOfInsuredPeople:
          (personData.travelers.children ? personData.travelers.children : 0) +
          (personData.travelers.adultsUpTo64 ? personData.travelers.adultsUpTo64 : 0) +
          (personData.travelers.adultsFrom65 ? personData.travelers.adultsFrom65 : 0) +
          (personData.travelers.complementary ? personData.travelers.complementary : 0),
        mainCountryOfTraveller: translateCountryCodeToCountry(personData.policyHolderCountry),
        travelPrice: personData.tripCost,
        confirmationDate: personData.bookingConfirmationDate.replaceAll('-', ''),
        paymentMethod: personData.paymentMethod,
        daysToDeparture: Helper.getDaysBetweenDates(undefined, personData.startDate),
        rentalProperty: personData.rental ? 'Ja' : 'Nein',
        travelDurationInDays: Helper.getDaysBetweenDates(personData.startDate, personData.endDate),
        calculatedValue: productsData.selectedProduct
          ? productsData.selectedProduct.quote.totalPremium
          : undefined,
        travelPriceType: 'Gesamtpreis',
        paymentInterval: !productsData.selectedProduct
          ? undefined
          : productsData.selectedProduct.isAnnual2
          ? '2-jaehrlich'
          : productsData.selectedProduct.isAnnual
          ? 'jaehrlich'
          : 'einmalig',
      },
      total: productsData.selectedProduct
        ? {
            transactionTotal: productsData.selectedProduct.quote.totalPremium,
          }
        : undefined,
      transactionID: settingsData.sessionId,
      vsnr: productsData.contract ? productsData.contract.contractId : undefined,
    },
  };
  return data;
};

const buildTrackingUsers: (personData: BookingState) => Array<TrackingUser> = (
  personData: BookingState,
) => {
  // policyholder
  let persons: Array<TrackingUser> = [];
  persons.push({
    profile: {
      address: {
        postalCode: personData.policyHolderPostalCode,
      },
      attributes: {
        birthday:
          personData.persons.length > 0 && personData.persons[0].birthDate
            ? personData.persons[0].birthDate.split('-')[0]
            : '', // 1955-12-25 -> 1955
        gender:
          personData.policyHolderSalutation === 'Mr'
            ? 'Mann'
            : personData.policyHolderSalutation === 'Ms'
            ? 'Frau'
            : '',
        role: 'endkunde',
        loggedIn: 'logged-out',
      },
    },
  });

  // additional persons
  if (personData.persons.length > 1) {
    personData.persons.slice(1).map((person) => {
      persons.push({
        profile: {
          address: {
            postalCode: '',
          },
          attributes: {
            birthday: person.birthDate ? person.birthDate.split('-')[0] : '', // 1955-12-25 -> 1955
            gender: '',
            role: 'endkunde',
            loggedIn: 'logged-out',
          },
        },
      });
    });
  }

  return persons;
};

const buildTrackingProducts: (
  pageName: string,
  trackingType: string,
  productsData: ProductState,
  personData: BookingState,
  currentSteps: number[],
  categoryTrackingId: string,
) => Array<TrackingProduct> = (
  pageName: string,
  trackingType: string,
  productsData: ProductState,
  personData: BookingState,
  currentSteps: number[],
  categoryTrackingId: string,
) => {
  // Tarif Page
  if (
    productsData.productPlan &&
    productsData.productPlan.length > 0 &&
    pageName &&
    pageName.indexOf('Berechnungsergebnis') !== -1
  ) {
    let availablePlans: ProductPlan = productsData.productPlan;
    let plans = availablePlans.filter((singlePlan: SinglePlan) => {
      return (
        singlePlan.quote &&
        (typeof singlePlan.category === 'undefined' ||
          singlePlan.category === personData.category) &&
        (typeof singlePlan.travelType === 'undefined' ||
          singlePlan.travelType === personData.meansOfTravel)
      );
    });

    return plans.map((filteredPlan) => {
      let insuranceDates = getInsuranceDates(filteredPlan.isAnnual ? true : false, personData);
      return {
        attributes: {
          insuranceAmount: filteredPlan.quote.totalPremium,
          insuranceStart: insuranceDates[0].split('-').join(''), // 2022-12-28 -> 20221228
          insuranceEnd: insuranceDates[1].split('-').join(''), // 2022-12-28 -> 20221228
          productOwner: 'ERV',
          productBranch: '',
          tariffOptions1: calcTariffOptions1(
            personData,
            currentSteps,
            trackingType,
            categoryTrackingId,
          ),
        },
        category: {
          primaryCategory: 'Reise',
        },
        productInfo: {
          productName:
            filteredPlan.tarifmatrix + '.' + filteredPlan.quote.quoteSegments[0].tariffCode,
        },
      };
    });
  }

  // Product selected
  if (productsData.selectedProduct) {
    let selectedProduct = productsData.selectedProduct;
    let insuranceDates = getInsuranceDates(selectedProduct.isAnnual ? true : false, personData);
    return [
      {
        attributes: {
          insuranceAmount: selectedProduct.quote.totalPremium,
          insuranceStart: insuranceDates[0].split('-').join(''), // 2022-12-28 -> 20221228,
          insuranceEnd: insuranceDates[1].split('-').join(''), // 2022-12-28 -> 20221228,
          productOwner: 'ERV',
          productBranch: getProductInsuranceType(selectedProduct),
          tariffOptions1: calcTariffOptions1(
            personData,
            currentSteps,
            trackingType,
            categoryTrackingId,
          ),
          productCombination:
            selectedProduct?.tarifmatrix + '.' + selectedProduct.quote.quoteSegments[0].tariffCode,
        },
        category: {
          primaryCategory: 'Reise',
        },
        productInfo: {
          productName:
            selectedProduct.tarifmatrix + '.' + selectedProduct.quote.quoteSegments[0].tariffCode,
        },
      },
    ];
  }
  // default
  let products: Array<TrackingProduct> = [
    {
      productInfo: {
        productName: 'Reise',
      },
      category: {
        primaryCategory: 'Reise',
      },
      attributes: {
        productOwner: 'ERV',
        tariffOptions1: calcTariffOptions1(
          personData,
          currentSteps,
          trackingType,
          categoryTrackingId,
        ),
        productBranch: '',
      },
    },
  ];

  return products;
};

const calcTariffOptions1 = (
  personData: BookingState,
  currentSteps: number[],
  trackingType: string,
  categoryTrackingId: string,
) => {
  let res = `Rechner=${trackingType}`;

  if (personData.category) {
    res = res + '|VWu=';
    if (personData.category) {
      res = res + categoryTrackingId;
    }
  }

  if (personData.meansOfTravel) {
    res = res + '|VeM=';
    switch (personData.meansOfTravel) {
      case 'air':
        res = res + 'FS';
        break;
      case 'car':
        res = res + 'SS';
        break;
      default:
        break;
    }
  }

  // excess and covidProtection is set on page 9, so track only from this page on
  if (currentSteps[currentSteps.length - 1] >= 9) {
    if (personData.excess === true) {
      res = res + '|SB=N';
    } else if (personData.excess === false) {
      res = res + '|SB=J';
    }

    if (personData.covidProtection === true) {
      res = res + '|PRC=J';
    } else if (personData.covidProtection === false) {
      res = res + '|PRC=N';
    }
  }

  return res;
};

const getInsuranceDates = (isAnnual: boolean, personData: BookingState): Array<string> => {
  if (isAnnual) {
    // today & today + 1 year - 1 day
    let today = new Date();
    let insuranceStart = today;
    let insuranceEnd = new Date();
    insuranceEnd.setDate(today.getDate() - 1);
    insuranceEnd.setFullYear(today.getFullYear() + 1);
    return [insuranceStart.toISOString().split('T')[0], insuranceEnd.toISOString().split('T')[0]];
  }
  // trip startdate & enddate
  return [personData.startDate, personData.endDate];
};

const getProductInsuranceType = (singlePlan: SinglePlan | undefined) => {
  if (singlePlan && singlePlan.coverageTypes && singlePlan.coverageTypes.indexOf('KV') > -1) {
    return 'Kranken';
  }
  return '';
};
const translateCountryCodeToCountry = (countryCode: string): string => {
  switch (countryCode) {
    case 'DE':
      return 'Deutschland';
    case 'AT':
      return 'Oesterreich';
    case 'BE':
      return 'Belgien';
    case 'DK':
      return 'Daenemark';
    case 'FR':
      return 'Frankreich';
    case 'IT':
      return 'Italien';
    case 'LU':
      return 'Luxemburg';
    case 'NL':
      return 'Niederlande';
    case 'PL':
      return 'Polen';
    case 'CZ':
      return 'Tschechien';
    default:
      return 'Deutschland';
  }
};

// custom hook
export const useTrackingPageLoad = (props: TrackingPageLoadType) => {
  const { trackEvent } = useTracking();
  const dispatch = useDispatch();
  const { setTrackingPageName } = bindActionCreators(actionCreators, dispatch);
  useEffect(() => {
    if (!props || !props.name) return;
    setTrackingPageName(props.name);
    let data: TrackingData = {
      event: 'PageLoaded',
      pageName: props.name,
    };
    if (props.conversionType) data.conversionType = props.conversionType;
    trackEvent(data);
  }, []);
};
